import React from "react";
import { t } from "@lingui/core/macro";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";

/**
 * Galerie d'images autonome avec lightbox (overlay plein écran + navigation
 * clavier ←/→/Échap + compteur). Réutilisable dans les pages détail du module
 * Travel (menu de restaurant, etc.).
 *
 * Props :
 *   - images: string[] — chemins d'assets (relatifs à ASSET_URL) ou URLs absolues.
 *
 * Styles encapsulés sous le préfixe `tr-mgal__` pour éviter toute collision avec
 * les styles spécifiques d'une page (ex. .tr-kdetail__*).
 */

const styles = `
.tr-mgal__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
  max-width: 720px;
}
.tr-mgal__thumb {
  width: 100%;
  aspect-ratio: 3/4;
  object-fit: cover;
  border-radius: 16px;
  display: block;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,.08);
  transition: transform .2s ease;
  background: #f3f4f6;
}
.tr-mgal__thumb:hover { transform: scale(1.02); }

.tr-mgal__lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
  cursor: zoom-out;
}
.tr-mgal__lightbox img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,.5);
}
.tr-mgal__lightbox-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255,255,255,.15);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: background .2s;
  z-index: 2;
}
.tr-mgal__lightbox-close:hover { background: rgba(255,255,255,.25); }
.tr-mgal__lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255,255,255,.15);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: background .2s, transform .2s;
  z-index: 2;
}
.tr-mgal__lightbox-nav:hover { background: rgba(255,255,255,.28); }
.tr-mgal__lightbox-nav--prev { left: 20px; }
.tr-mgal__lightbox-nav--next { right: 20px; }
.tr-mgal__lightbox-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255,255,255,.12);
  color: white;
  font-size: 13px;
  font-weight: 500;
  z-index: 2;
}
@media (max-width: 640px) {
  .tr-mgal__lightbox-nav { width: 44px; height: 44px; }
  .tr-mgal__lightbox-nav--prev { left: 10px; }
  .tr-mgal__lightbox-nav--next { right: 10px; }
}
`;

const srcOf = (img) => (/^https?:\/\//.test(img) ? img : `${ASSET_URL}${img}`);

export default function MenuGallery({ images }) {
    const list = Array.isArray(images) ? images.filter(Boolean) : [];
    const [lightboxIndex, setLightboxIndex] = React.useState(null);

    const closeLightbox = () => setLightboxIndex(null);
    const showPrev = React.useCallback(() => {
        setLightboxIndex((i) => (i == null ? null : (i - 1 + list.length) % list.length));
    }, [list.length]);
    const showNext = React.useCallback(() => {
        setLightboxIndex((i) => (i == null ? null : (i + 1) % list.length));
    }, [list.length]);

    React.useEffect(() => {
        if (lightboxIndex == null) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowLeft") showPrev();
            else if (e.key === "ArrowRight") showNext();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightboxIndex, showPrev, showNext]);

    if (list.length === 0) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <div className="tr-mgal__grid">
                {list.map((img, i) => (
                    <img
                        key={i}
                        src={srcOf(img)}
                        alt={`Menu ${i + 1}`}
                        className="tr-mgal__thumb"
                        loading="lazy"
                        onClick={() => setLightboxIndex(i)}
                    />
                ))}
            </div>

            {lightboxIndex != null && list[lightboxIndex] && (
                <div className="tr-mgal__lightbox" onClick={closeLightbox}>
                    <button
                        className="tr-mgal__lightbox-close"
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                        aria-label={t`Fermer`}
                    >
                        {renderIcon({ name: "IconX", source: "tabler" }, 22)}
                    </button>
                    {list.length > 1 && (
                        <>
                            <button
                                className="tr-mgal__lightbox-nav tr-mgal__lightbox-nav--prev"
                                onClick={(e) => { e.stopPropagation(); showPrev(); }}
                                aria-label={t`Image précédente`}
                            >
                                {renderIcon({ name: "IconChevronLeft", source: "tabler" }, 28)}
                            </button>
                            <button
                                className="tr-mgal__lightbox-nav tr-mgal__lightbox-nav--next"
                                onClick={(e) => { e.stopPropagation(); showNext(); }}
                                aria-label={t`Image suivante`}
                            >
                                {renderIcon({ name: "IconChevronRight", source: "tabler" }, 28)}
                            </button>
                            <span className="tr-mgal__lightbox-counter">
                                {lightboxIndex + 1} / {list.length}
                            </span>
                        </>
                    )}
                    <img
                        src={srcOf(list[lightboxIndex])}
                        alt=""
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </>
    );
}
